import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Space,
  Tag,
  Input,
  Row,
  Col,
  message,
  Typography,
  Divider,
  Alert,
  Statistic,
  Progress,
  Badge,
  Timeline,
  Tooltip,
  Popconfirm,
  Button,
} from 'antd';
import {
  ScanOutlined,
  BarcodeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  TruckOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import type { PackingOrder } from '../../types/packing';
import apiService from '../../../../services/apiService';
import { playErrorSound } from '../../../../utils/soundUtils';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

interface PickupScanInterfaceProps {
  onUpdateOrder?: () => void;
}

interface ScanResult {
  barcode: string;
  success: boolean;
  order?: PackingOrder;
  error?: string;
  timestamp: Date;
}

const PickupScanInterface: React.FC<PickupScanInterfaceProps> = ({
  onUpdateOrder
}) => {
  const { t } = useTranslation();
  const [scanning, setScanning] = useState(true);
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([]);
  const [currentOrder, setCurrentOrder] = useState<PackingOrder | null>(null);
  const [scanInputValue, setScanInputValue] = useState('');
  
  // Scanner detection
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastScanTime, setLastScanTime] = useState(0);
  const [isRealScannerMode, setIsRealScannerMode] = useState(false);
  
  const scanInputRef = useRef<any>(null);

  const handleClearScanHistory = () => {
    setScanHistory([]);
    message.success(t('已清空掃描歷史'));
  };

  useEffect(() => {
    // Focus on scan input when component mounts
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, []);

  // Hardware barcode scanner integration
  useEffect(() => {
    let scannerTimeout: NodeJS.Timeout;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const currentTime = Date.now();
      
      // Check if this could be from a barcode scanner (rapid input)
      if (currentTime - lastScanTime < 100) {
        setIsRealScannerMode(true);
      }
      
      setLastScanTime(currentTime);

      // Handle Enter key (scanner suffix)
      if (event.key === 'Enter') {
        event.preventDefault();
        if (scanBuffer.trim().length > 0) {
          handleScan(scanBuffer.trim());
          setScanBuffer('');
          setIsRealScannerMode(false);
        }
        return;
      }

      // Handle regular characters (build barcode)
      if (event.key.length === 1) {
        event.preventDefault();
        setScanBuffer(prev => prev + event.key);
        
        // Clear buffer after delay (in case scanner doesn't send Enter)
        clearTimeout(scannerTimeout);
        scannerTimeout = setTimeout(() => {
          if (scanBuffer.length > 8) { // Minimum barcode length
            handleScan(scanBuffer.trim());
          }
          setScanBuffer('');
          setIsRealScannerMode(false);
        }, 300);
      }
    };

    // Add global keyboard listener for barcode scanner
    if (scanning) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(scannerTimeout);
    };
  }, [scanning, scanBuffer, lastScanTime]);

  // Visual feedback for scanner detection
  useEffect(() => {
    if (isRealScannerMode && scanBuffer.length > 0) {
      // Show visual feedback that scanner is detected
      const indicator = document.createElement('div');
      indicator.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #52c41a;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        z-index: 9999;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      `;
      indicator.textContent = t('🔍 掃描中... {{scanBuffer}}', { scanBuffer });
      document.body.appendChild(indicator);
      
      setTimeout(() => {
        if (document.body.contains(indicator)) {
          document.body.removeChild(indicator);
        }
      }, 2000);
    }
  }, [isRealScannerMode, scanBuffer]);

  const handleScan = async (barcode: string) => {
    if (!barcode || !barcode.trim()) return;

    setScanning(true);
    const searchTerm = barcode.trim();

    try {
      console.log('[PICKUP] 开始扫描，运单号:', searchTerm);

      // 使用優化的單一 API 端點，一次完成查找、驗證和狀態更新
      const response: any = await apiService.scanForPickup(searchTerm);

      console.log('[PICKUP] API响应:', response);

      if (!response.success) {
        throw new Error(response.message || t('掃描失敗'));
      }

      const matchedOrder = response.order;

      console.log('[PICKUP] 攬收成功，訂單:', matchedOrder?.orderNumber);

      const scanResult: ScanResult = {
        barcode: searchTerm,
        success: true,
        order: matchedOrder,
        timestamp: new Date(),
      };
      setScanHistory(prev => [scanResult, ...prev]);
      setCurrentOrder(matchedOrder);

      message.success({
        content: t('攬收成功！訂單 {{orderRef}} 已更新為待攬收狀態', {
          orderRef: matchedOrder?.orderNumber || searchTerm,
        }),
        duration: 2,
      });

      // 通知父組件刷新
      if (onUpdateOrder) {
        onUpdateOrder();
      }

      // 清空輸入框，準備下一次掃描（無需延遲重置整個頁面）
      setScanInputValue('');

      // 聚焦輸入框以便連續掃描
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }

    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || String(err ?? t('系統錯誤'));
      const scanResult: ScanResult = {
        barcode: searchTerm,
        success: false,
        error: errorMsg,
        timestamp: new Date(),
      };
      setScanHistory(prev => [scanResult, ...prev]);
      playErrorSound();
      message.error(t('掃描失敗：{{errorMsg}}', { errorMsg }));

      // 即使失敗也清空輸入框，準備重新掃描
      setScanInputValue('');
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    } finally {
      setScanning(false);
      // 快速恢復掃描狀態（無需延遲）
      setScanning(true);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'default',
      'packing': 'blue',
      'packed': 'green',
      'ready_for_pickup': 'orange',
      'shipped': 'purple',
      'delivered': 'success',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    // packed/ready_for_pickup/shipped/delivered are persisted outbound-order
    // status values, never wrapped in t() per the glossary.
    const texts: Record<string, string> = {
      'pending': t('待處理'),
      'packing': t('包裝中'),
      'packed': '已包裝',
      'ready_for_pickup': '待攬收',
      'shipped': '已發貨',
      'delivered': '已收貨',
    };
    return texts[status] || status;
  };

  return (
    <div style={{ padding: '24px' }}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <TruckOutlined /> {t('攬收掃描操作')}
        </Title>
        <Text type="secondary">
          {t('請掃描運單條碼進行攬收確認')}
        </Text>
      </div>

      {/* Alert */}
      <Alert
        message={t('攬收操作說明')}
        description={t('請掃描已包裝訂單的運單條碼。掃描成功後，訂單狀態將從「已包裝」更新為「待攬收」，表示訂單已準備好由配送團隊取件。')}
        type="info"
        showIcon
        closable
        style={{ marginBottom: '24px' }}
      />

      {/* Main Content */}
      <Row gutter={16}>
        <Col span={16}>
          {/* Scan Input */}
          <Card
            title={
              <Space>
                <ScanOutlined />
                <span>{t('掃描區域')}</span>
                {currentOrder && (
                  <Tag color={getStatusColor(currentOrder.status)}>
                    {getStatusText(currentOrder.status)}
                  </Tag>
                )}
              </Space>
            }
            style={{ marginBottom: '16px' }}
          >
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <BarcodeOutlined 
                style={{ 
                  fontSize: '64px', 
                  color: scanning ? '#1890ff' : '#ccc',
                  marginBottom: '16px'
                }} 
              />
              <div style={{ marginBottom: '24px' }}>
                <Input
                  ref={scanInputRef}
                  placeholder={t('掃描運單條碼...')}
                  size="large"
                  prefix={<ScanOutlined />}
                  value={scanInputValue}
                  onChange={(e) => setScanInputValue(e.target.value)}
                  onPressEnter={() => {
                    handleScan(scanInputValue);
                  }}
                  autoFocus
                  style={{ maxWidth: '500px' }}
                />
              </div>
              <Text type="secondary">
                {t('將掃描槍對準運單條碼，或手動輸入後按 Enter')}
              </Text>
            </div>
          </Card>

          {/* Current Order Info */}
          {currentOrder && (
            <Card
              title={
                <Space>
                  <InboxOutlined />
                  <span>{t('當前訂單信息')}</span>
                </Space>
              }
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic 
                    title={t('訂單編號')} 
                    value={currentOrder.orderNumber}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title={t('運單編號')} 
                    value={currentOrder.waybillNumber}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title={t('客戶名稱')} 
                    value={currentOrder.customerName}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Col>
                <Col span={12}>
                  <div>
                    <Text strong>{t('收件人：')}</Text>
                    <Text>{currentOrder.receiverName}</Text>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Text strong>{t('聯絡電話：')}</Text>
                    <Text>{currentOrder.receiverContact}</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Text strong>{t('配送公司：')}</Text>
                    <Text>{currentOrder.deliveryCompany || '-'}</Text>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Text strong>{t('包裹重量：')}</Text>
                    <Text>{currentOrder.packageWeight} kg</Text>
                  </div>
                </Col>
              </Row>
            </Card>
          )}
        </Col>

        <Col span={8}>
          {/* Scan History */}
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                <span>{t('掃描歷史')}</span>
                <Badge count={scanHistory.length} />
              </Space>
            }
            extra={
              <Tooltip title={t('清空掃描歷史')}>
                <Popconfirm
                  title={t('確認清空掃描歷史？')}
                  okText={t('清空')}
                  cancelText={t('取消')}
                  onConfirm={handleClearScanHistory}
                >
                  <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
                </Popconfirm>
              </Tooltip>
            }
          >
            <Timeline
              mode="left"
              style={{ maxHeight: '500px', overflowY: 'auto', wordBreak: 'break-word' }}
            >
              {scanHistory.map((scan, index) => (
                <Timeline.Item
                  key={index}
                  color={scan.success ? 'green' : 'red'}
                  dot={
                    scan.success ? 
                    <CheckCircleOutlined style={{ color: 'green' }} /> :
                    <ExclamationCircleOutlined style={{ color: 'red' }} />
                  }
                >
                  <div style={{ fontSize: '12px', padding: '2px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%'
                      }}
                    >
                      {scan.barcode}
                    </div>
                    <div style={{ color: '#666' }}>
                      {moment(scan.timestamp).format('HH:mm:ss')}
                    </div>
                    {scan.success && scan.order && (
                      <div style={{ color: 'green', marginTop: '4px' }}>
                        {t('✓ 訂單')} {scan.order.orderNumber} {t('已更新為待攬收')}
                      </div>
                    )}
                    {!scan.success && (
                      <div
                        style={{
                          background: '#fff2f0',
                          border: '1px solid #ffccc7',
                          borderRadius: 4,
                          padding: 12,
                          marginTop: 4
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#cf1322' }}>
                          <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                          {t('掃描失敗')}
                        </div>
                        <div style={{ marginTop: 6, color: '#cf1322' }}>
                          <span style={{ marginRight: 6 }}>❌</span>
                          {scan.error || t('未知錯誤')}
                        </div>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {/* Statistics */}
          <Card
            title={t('今日統計')}
            style={{ marginTop: '16px' }}
          >
            <Statistic 
              title={t('已攬收訂單')} 
              value={scanHistory.filter(s => s.success).length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Divider />
            <Statistic 
              title={t('失敗次數')} 
              value={scanHistory.filter(s => !s.success).length}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
            {scanHistory.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text strong>{t('成功率')}</Text>
                  <Progress 
                    percent={Math.round((scanHistory.filter(s => s.success).length / scanHistory.length) * 100)}
                    status="active"
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                  />
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PickupScanInterface;
